import React from 'react';
import * as R from 'ramda';
import {
  // eslint-disable-next-line no-restricted-imports
  createTheme,
  Palette as MuiPalette,
  Breakpoint,
  Theme,
  ThemeOptions,
} from '@mui/material';
import {
  ThemeProvider,
  CleanThemeProvider,
  defaultThemeOptions,
  defaultThemeOptionsAtBreakpoint,
} from '@practicegenius/pg-ui/src/base/Material/theme';
import {
  DeepPartial,
  isUndefined,
} from '@practicegenius/pg-util/src/TypescriptUtils';
import {Practice} from '@practicegenius/domain/src/data/practice';
import {
  baseMembersThemeOptions,
  FALLBACK_UNAUTH_COLOR,
  getColorsForMemberThemeId,
  getExpandedPaletteForColor,
} from '@practicegenius/pg-ui/src/base/Material/memberTheme';
import {inMobileApp} from '@practicegenius/pg-ui/src/utils/MobileApp';
import {useIsNextJs} from '@practicegenius/next-utils/src/useIsNextJs';
import {useUserProfile} from '../../components/UserProfileProvider';
import ServiceBrand from '../../utils/ServiceBrand';

const sitesDotThemeOptions: Theme = createTheme(baseMembersThemeOptions);

type ThemeCustomizationOptions = {
  atBreakpoint?: Breakpoint;
  /** User-selected theme id. Undefined for unauthenticated */
  themeId?: number;
  palette?: DeepPartial<Theme['palette']>;
};

/**
 * While we've deprecated all our custom themes here in favor
 * of `pg-ui`, `sites.` has a special need to control its own
 * breakpoints (thanks a bunch Foundation).  This little
 * helper will do all the heavy lifting for ya! 😄
 */
export const createSitesTheme = ({
  atBreakpoint,
  themeId,
  palette: paletteOverride = {},
}: ThemeCustomizationOptions = {}): Theme => {
  const baseThemeOptions = isUndefined(atBreakpoint)
    ? defaultThemeOptions
    : defaultThemeOptionsAtBreakpoint(atBreakpoint);

  const memberThemeColors = (() => {
    if (!themeId) return {};
    const colors = getColorsForMemberThemeId(themeId);
    return {
      palette: {
        primary: getExpandedPaletteForColor(colors.primary),
        secondary: getExpandedPaletteForColor(colors.secondary),
      },
    };
  })();

  const sitesDotTheme = createTheme(
    R.reduce(
      R.mergeDeepRight,
      // @ts-ignore
      {},
      [
        R.evolve(
          {
            // We need to avoid using the 50-900 values from the pg-ui theme here, as they leak into hover states and will lead to weird UX
            palette: {
              primary: R.pick(['main']),
              secondary: R.pick(['main']),
            },
          },
          // @ts-expect-error
          R.omit(['overrides'], baseThemeOptions),
        ),
        sitesDotThemeOptions,
        // Override palette with member theme colors if defined
        memberThemeColors,
      ],
    ) as ThemeOptions,
  );

  // I expect that one day we will align the mobile app and standard web experiences.
  // Until then, we need to show the old color so we don't clash with the legacy CSS.
  /** Get MUI theme colors for the unauthenticated app experience */
  const unauthThemeColors = (() => {
    const bodyEl = document.querySelector('body');

    const makeResult = (primaryColor: string, secondaryColor: string) => ({
      primary: getExpandedPaletteForColor(primaryColor),
      secondary: getExpandedPaletteForColor(secondaryColor),
    });

    if (!bodyEl)
      return makeResult(
        FALLBACK_UNAUTH_COLOR.primary,
        FALLBACK_UNAUTH_COLOR.secondary,
      );

    // Get legacy theme colors by parsing the CSS variables
    let primaryColor = getComputedStyle(bodyEl)
      .getPropertyValue('--template-color-1')
      .trim();
    let secondaryColor = getComputedStyle(bodyEl)
      .getPropertyValue('--template-color-8')
      .trim();
    if (primaryColor === '') primaryColor = FALLBACK_UNAUTH_COLOR.primary;
    if (secondaryColor === '') secondaryColor = FALLBACK_UNAUTH_COLOR.secondary;

    // Special case: override theme colors in mobile app to make them consistent across all practices
    if (inMobileApp)
      primaryColor = ServiceBrand.select({
        prh: '#00A2B8',
        hubbux: '#E02020',
        culturello: '#0ebbc5',
      });

    return makeResult(primaryColor, secondaryColor);
  })();

  // theme palette primary and secondary for unauthenticated views
  const unauthThemeOverrides = {
    primary: {
      ...unauthThemeColors.primary,
      contrastText: '#fff',
    },
    secondary: {
      ...unauthThemeColors.secondary,
      contrastText: '#fff',
    },
    action: {
      hoverOpacity: 0.08,
      focusOpacity: 0.24,
    },
  };

  const authThemeOverrides = {};

  const overrides = R.mergeDeepRight(
    sitesDotTheme.palette ?? {},
    themeId !== undefined ? authThemeOverrides : unauthThemeOverrides,
  );

  return {
    ...sitesDotTheme,
    ...{
      // TODO this might need a touchup for the members-app
      /**
       * Currently, when unauthenticated and in the mobile app we override the primary and secondary colors
       *   to a global color scheme. When this is used in Next.js the practice colors are provided via the
       *   paletteOverride prop. This means that in the members app the practice color scheme will take priority
       *   over the global color scheme when unauthenticated. We'll just need to see what the product team thinks
       *   of the updated experience
       */
      palette: [sitesDotTheme.palette, overrides, paletteOverride].reduce(
        R.mergeDeepRight,
        {},
      ) as MuiPalette,
    },
  };
};

type ThemeProviderProps = {breakpoint?: Breakpoint};

export const UnauthenticatedThemeProvider: React.FCC<{
  breakpoint?: Breakpoint;
  practiceColors?: Practice['colors'];
}> = ({children, breakpoint, practiceColors}) => {
  const isNextJs = useIsNextJs();
  const ThemeProviderImpl = isNextJs ? CleanThemeProvider : ThemeProvider;

  const palette = !practiceColors
    ? undefined
    : {
        primary: {main: practiceColors.color1},
        secondary: {main: practiceColors.color8},
      };
  return (
    <ThemeProviderImpl
      theme={createSitesTheme({atBreakpoint: breakpoint, palette})}
    >
      {children}
    </ThemeProviderImpl>
  );
};

/** Override the Standard Unauthenticated theme with user-defined overrides from their selected theme */
export const AuthenticatedThemeProvider: React.FCC<ThemeProviderProps> = ({
  children,
  breakpoint,
}) => {
  const user = useUserProfile();
  const isNextJs = useIsNextJs();
  const ThemeProviderImpl = isNextJs ? CleanThemeProvider : ThemeProvider;
  return (
    <ThemeProviderImpl
      theme={createSitesTheme({
        atBreakpoint: breakpoint,
        themeId: user.themeId,
      })}
    >
      {children}
    </ThemeProviderImpl>
  );
};
